import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { usePermissions } from "./PermissionContext";
import { useTheme } from "./ThemeContext";

function Sidebar() {
  const { hasPermission } = usePermissions();
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    // Clear all localStorage values related to user session
    localStorage.removeItem("user");
    localStorage.removeItem("userData");
    localStorage.removeItem("userPermissions");
    localStorage.removeItem("token");
    localStorage.removeItem("keepLoggedIn");
    localStorage.removeItem("schemeType");
    localStorage.removeItem("preferredLanguage");
    localStorage.removeItem("theme");
    
    // Clear any other session-related data
    localStorage.clear();
    
    navigate("/login");
  };

  const handleLogoutClick = (e) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Handle navbar hamburger toggle
  useEffect(() => {
    const handleToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const body = document.body;
      const currentSize = body.getAttribute("data-sidebar-size");


      if (currentSize === "collapsed") {
        body.removeAttribute("data-sidebar-size");
      } else {
        body.setAttribute("data-sidebar-size", "collapsed");
      }
    };

    const setupToggle = () => {
      const toggleBtn = document.getElementById("togglemenu");

      if (toggleBtn) {

        // Remove any existing event listeners
        toggleBtn.removeEventListener("click", handleToggle);

        // Add our event listener
        toggleBtn.addEventListener("click", handleToggle, true);

        return true;
      }
      return false;
    };

    // Try immediate setup
    if (!setupToggle()) {
      // console.log("Toggle button not found immediately, retrying...");

      // Retry after short delays
      const timeouts = [];
      [100, 300, 500, 1000, 2000].forEach((delay) => {
        const timeout = setTimeout(() => {
          if (setupToggle()) {
            // console.log(`Toggle setup successful after ${delay}ms`);
            // Clear remaining timeouts
            timeouts.forEach((t) => clearTimeout(t));
          }
        }, delay);
        timeouts.push(timeout);
      });
    }

    return () => {
      const toggleBtn = document.getElementById("togglemenu");
      if (toggleBtn) {
        toggleBtn.removeEventListener("click", handleToggle, true);
        // console.log("Cleanup: removed event listener");
      }
    };
  }, []);

  return (
    <>
      <div
        className={`startbar d-print-none ${
          dark ? "sidebar-dark" : "sidebar-light"
        }`}
        style={{ zIndex: 1000 }}
      >
        {/* Brand Section */}
        <div className="brand">
          <NavLink to="/dashboard" className="logo">
            <div className="logo-container">
              <img
                src="assets/images/or_logo.png"
                alt="logo"
                className="logo-sm"
              />
              
              {/* <span className="logo-text ms-2 fw-bold">Admin Panel</span> */}
            </div>
          </NavLink>
        </div>

        <div className="half-divider"></div>
        
        <div className="startbar-menu">
          <div
            className="startbar-collapse"
            id="startbarCollapse"
            data-simplebar=""
          >
            <div className="d-flex align-items-start flex-column w-100">
              <ul className="navbar-nav mb-auto w-100">
                {/* Dashboard */}
                <li className="nav-item">
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      `nav-link sidebar-link ${isActive ? "active" : ""}`
                    }
                  >
                    <div className="nav-link-content">
                      <div className="nav-icon-wrapper me-3">
                        <i className="fas fa-home nav-icon text-primary" />
                      </div>
                      <span className="nav-text">Dashboard</span>
                      <div className="nav-indicator"></div>
                    </div>
                  </NavLink>
                </li>

                {/* Customer Management */}
                {hasPermission("customer") && (
                  <>
                    <li className="nav-item">
                      <NavLink
                        to="/CustomerList"
                        className={({ isActive }) =>
                          `nav-link sidebar-link ${isActive ? "active" : ""}`
                        }
                      >
                        <div className="nav-link-content">
                          <div className="nav-icon-wrapper me-3">
                            <i className="fas fa-users nav-icon text-success" />
                          </div>
                          <span className="nav-text">Members</span>
                          <div className="nav-indicator"></div>
                        </div>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink
                        to="/TemporaryCustomer"
                        className={({ isActive }) =>
                          `nav-link sidebar-link ${isActive ? "active" : ""}`
                        }
                      >
                        <div className="nav-link-content">
                          <div className="nav-icon-wrapper me-3">
                            <i className="fas fa-user-plus nav-icon text-info" />
                          </div>
                          <span className="nav-text">On-boarding</span>
                          <div className="nav-indicator"></div>
                        </div>
                      </NavLink>
                    </li>
                  </>
                )}

                {/* Transactions */}
                {hasPermission("payment") && (
                  <li className="nav-item">
                    <NavLink
                      to="/PaymentList"
                      className={({ isActive }) =>
                        `nav-link sidebar-link ${isActive ? "active" : ""}`
                      }
                    >
                      <div className="nav-link-content">
                        <div className="nav-icon-wrapper me-3">
                          <i className="fas fa-credit-card nav-icon text-warning" />
                        </div>
                        <span className="nav-text">Transactions</span>
                        <div className="nav-indicator"></div>
                      </div>
                    </NavLink>
                  </li>
                )}

                {/* Reports */}
                {hasPermission("report") && (
                  <>
                    <li className="nav-item">
                      <NavLink
                        to="/report"
                        className={({ isActive }) =>
                          `nav-link sidebar-link ${isActive ? "active" : ""}`
                        }
                      >
                        <div className="nav-link-content">
                          <div className="nav-icon-wrapper me-3">
                            <i className="fas fa-chart-line nav-icon text-danger" />
                          </div>
                          <span className="nav-text">Reports</span>
                          <div className="nav-indicator"></div>
                        </div>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink
                        to="/today-report"
                        className={({ isActive }) =>
                          `nav-link sidebar-link ${isActive ? "active" : ""}`
                        }
                      >
                        <div className="nav-link-content">
                          <div className="nav-icon-wrapper me-3">
                            <i className="fas fa-calendar-day nav-icon text-info" />
                          </div>
                          <span className="nav-text">Today Report</span>
                          <div className="nav-indicator"></div>
                        </div>
                      </NavLink>
                    </li>
                  </>
                )}

                {/* Half-line divider before Employee */}
                <li className="nav-item">
                  <div className="half-divider"></div>
                </li>

                {/* Employee Management */}
                {hasPermission("employee") && (
                  <li className="nav-item">
                    <NavLink
                      to="/EmployeeList"
                      className={({ isActive }) =>
                        `nav-link sidebar-link ${isActive ? "active" : ""}`
                      }
                    >
                      <div className="nav-link-content">
                        <div className="nav-icon-wrapper me-3">
                          <i className="fas fa-user-tie nav-icon text-purple" />
                        </div>
                        <span className="nav-text">Employees</span>
                        <div className="nav-indicator"></div>
                      </div>
                    </NavLink>
                  </li>
                )}

                {/* Live Price Management */}
                <li className="nav-item">
                  <NavLink
                    to="/LivePrice"
                    className={({ isActive }) =>
                      `nav-link sidebar-link ${isActive ? "active" : ""}`
                    }
                  >
                    <div className="nav-link-content">
                      <div className="nav-icon-wrapper me-3">
                        <i className="fas fa-coins nav-icon text-warning" />
                      </div>
                      <span className="nav-text">Live Price</span>
                      <div className="nav-indicator"></div>
                    </div>
                  </NavLink>
                </li>

                {/* Showroom Rate Management */}
                <li className="nav-item">
                  <NavLink
                    to="/ShowroomRate"
                    className={({ isActive }) =>
                      `nav-link sidebar-link ${isActive ? "active" : ""}`
                    }
                  >
                    <div className="nav-link-content">
                      <div className="nav-icon-wrapper me-3">
                        <i className="fas fa-store nav-icon text-info" />
                      </div>
                      <span className="nav-text">Showroom Rate</span>
                      <div className="nav-indicator"></div>
                    </div>
                  </NavLink>
                </li>

                {/* Catalogue Management */}
                <li className="nav-item">
                  <NavLink
                    to="/Catalogue"
                    className={({ isActive }) =>
                      `nav-link sidebar-link ${isActive ? "active" : ""}`
                    }
                  >
                    <div className="nav-link-content">
                      <div className="nav-icon-wrapper me-3">
                        <i className="fas fa-book nav-icon text-warning" />
                      </div>
                      <span className="nav-text">Catalogue</span>
                      <div className="nav-indicator"></div>
                    </div>
                  </NavLink>
                </li>

              </ul>
            </div>
          </div>
        </div>

        {/* Logout Button - Positioned at bottom */}
        <div className="sidebar-footer">
          <button
            className="nav-link sidebar-link logout-btn"
            onClick={handleLogoutClick}
          >
            <div className="nav-link-content">
              <div className="nav-icon-wrapper me-3">
                <i className="fas fa-sign-out-alt nav-icon logout-icon text-danger" />
              </div>
              <span className="nav-text">Logout</span>
              <div className="nav-indicator"></div>
            </div>
          </button>
        </div>
      </div>
      <div className="startbar-overlay d-print-none" />

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-modal-header">
              <h5>Confirm Logout</h5>
            </div>
            <div className="logout-modal-body">
              <p>Are you sure you want to logout?</p>
            </div>
            <div className="logout-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelLogout}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Sidebar Styling */}
      <style jsx>{`
        /* Custom purple color */
        .text-purple {
          color: #6f42c1 !important;
        }

        /* Basic sidebar styling with hover expand */
        .startbar {
          background: ${dark ? "#1a1d29" : "#ffffff"};
          border-right: 1px solid
            ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
          box-shadow: 2px 0 8px rgba(0, 0, 0, ${dark ? "0.3" : "0.1"});
          transition: all 0.3s ease;
        }

        /* Hover to expand functionality */
        body[data-sidebar-size="collapsed"] .startbar:hover {
          width: 250px !important;
          position: fixed !important;
          z-index: 1000 !important;
          left: 0 !important;
          top: 0 !important;
          height: 100vh !important;
        }

        /* Restore original layout when expanded via hover */
        body[data-sidebar-size="collapsed"] .startbar:hover .logo-text,
        body[data-sidebar-size="collapsed"] .startbar:hover .nav-text,
        body[data-sidebar-size="collapsed"] .startbar:hover .menu-label-text {
          display: block !important;
          opacity: 1;
          transition: opacity 0.3s ease 0.1s;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .nav-link-content {
          justify-content: flex-start !important;
          padding: 0.5rem !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .nav-icon-wrapper {
          margin-right: 0.75rem !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .nav-item {
          margin: 0.125rem 1rem !important;
          width: calc(100% - 2rem) !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .sidebar-link {
          width: 100% !important;
          justify-content: flex-start !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .menu-label {
          margin: 0.25rem 1rem 0.5rem 1rem !important;
          padding: 0.75rem !important;
          justify-content: flex-start !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .brand {
          padding: 1rem !important;
          justify-content: flex-start !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .logo {
          justify-content: flex-start !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .logo-container {
          justify-content: flex-start !important;
        }

        body[data-sidebar-size="collapsed"] .startbar:hover .sidebar-footer {
          display: block !important;
        }

        /* Brand section */
        .brand {
          padding: 1rem;
          background: ${dark
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.02)"};
        }

        .logo {
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          padding: 0.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .logo:hover {
          background: ${dark
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.05)"};
          transform: translateY(-1px);
        }

        .logo-container {
          display: flex;
          align-items: center;
        }

        .logo-sm {
          width: 32px;
          height: 32px;
          border-radius: 6px;
        }

        /* Larger logo when sidebar is expanded */
        body:not([data-sidebar-size="collapsed"]) .startbar .logo-sm {
          width: 90px !important;
          height: 70px !important;
          border-radius: 8px !important;
        }

        /* Larger logo when hovering over collapsed sidebar */
        body[data-sidebar-size="collapsed"] .startbar:hover .logo-sm {
          width: 90px !important;
          height: 70px !important;
          border-radius: 8px !important;
        }

        /* Center logo during hover on collapsed sidebar */
        body[data-sidebar-size="collapsed"] .startbar:hover .brand {
          justify-content: center !important;
        }
        
        body[data-sidebar-size="collapsed"] .startbar:hover .logo {
          justify-content: center !important;
        }
        
        body[data-sidebar-size="collapsed"] .startbar:hover .logo-container {
          justify-content: center !important;
        }

        .logo-text {
          font-size: 1rem;
          font-weight: 600;
          color: ${dark ? "#ffffff" : "#2c3e50"};
        }

        /* Half-width dividers */
        .brand-divider,
        .half-divider {
          height: 1px;
          background: ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
          margin: 0.75rem 1rem 0.5rem 1rem;
          width: calc(100% - 2rem);
        }

        .brand-divider {
          margin-top: 1rem;
        }

        /* Menu sections */
        .startbar-menu {
          padding: 0.5rem 0;
        }

        .menu-label {
          margin: 0.25rem 1rem 0.5rem 1rem;
          padding: 0.75rem;
        }

        .menu-label-text {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: ${dark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)"};
        }

        .menu-label-icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #007bff, #0056b3);
          border-radius: 6px;
          color: white;
          font-size: 1rem;
        }

        /* Navigation items */
        .nav-item {
          margin: 0.125rem 1rem;
        }

        /* Ensure proper layout when sidebar is normally expanded */
        body:not([data-sidebar-size="collapsed"]) .nav-link-content {
          justify-content: flex-start !important;
          padding: 0.5rem !important;
          display: flex !important;
          align-items: center !important;
        }

        body:not([data-sidebar-size="collapsed"]) .nav-icon-wrapper {
          margin-right: 0.75rem !important;
          flex-shrink: 0;
        }

        body:not([data-sidebar-size="collapsed"]) .nav-item {
          margin: 0.125rem 1rem !important;
        }

        body:not([data-sidebar-size="collapsed"]) .sidebar-link {
          width: 100% !important;
        }

        body:not([data-sidebar-size="collapsed"]) .brand {
          padding: 1rem !important;
        }

        body:not([data-sidebar-size="collapsed"]) .logo-container {
          justify-content: flex-start !important;
        }

        .sidebar-link {
          display: block;
          padding: 0;
          text-decoration: none !important;
          border-radius: 8px;
          transition: background-color 0.15s ease;
          background: transparent;
          border: none;
          color: inherit !important;
        }

        .sidebar-link:hover {
          background: ${dark
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.05)"};
          text-decoration: none !important;
          color: inherit !important;
        }

        .sidebar-link.active {
          background: ${dark
            ? "rgba(0, 123, 255, 0.15)"
            : "rgba(0, 123, 255, 0.1)"};
        }

        .nav-link-content {
          display: flex;
          align-items: center;
          padding: 0.5rem;
          position: relative;
        }

        .nav-icon-wrapper {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${dark
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(255, 255, 255, 0.6)"};
          border-radius: 6px;
          transition: background-color 0.15s ease;
          flex-shrink: 0;
        }

        .sidebar-link:hover .nav-icon-wrapper {
          background: ${dark
            ? "rgba(255, 255, 255, 0.12)"
            : "rgba(255, 255, 255, 0.8)"};
        }

        .sidebar-link.active .nav-icon-wrapper {
          background: rgba(255, 255, 255, 0.9);
        }

        /* Enhanced active state for collapsed sidebar */
        body[data-sidebar-size="collapsed"] .sidebar-link.active {
          background: transparent !important;
          border: none !important;
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper {
          background: rgba(255, 255, 255, 0.1) !important;
          border: 0.5px ;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        }

        /* Color-specific borders for active state in collapsed mode */
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon.text-primary {
          color: #007bff !important;
        }
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper:has(.text-primary),
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper {
          border-color: var(--icon-color, #007bff) !important;
          box-shadow: 0 0 8px var(--icon-shadow, rgba(0, 123, 255, 0.3)) !important;
        }

        /* Individual icon color variables for collapsed active state */
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active:has(.text-primary) {
          --icon-color: #007bff;
          --icon-shadow: rgba(0, 123, 255, 0.3);
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active:has(.text-success) {
          --icon-color: #28a745;
          --icon-shadow: rgba(40, 167, 69, 0.3);
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active:has(.text-info) {
          --icon-color: #17a2b8;
          --icon-shadow: rgba(23, 162, 184, 0.3);
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active:has(.text-warning) {
          --icon-color: #ffc107;
          --icon-shadow: rgba(255, 193, 7, 0.3);
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active:has(.text-danger) {
          --icon-color: #dc3545;
          --icon-shadow: rgba(220, 53, 69, 0.3);
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active:has(.text-purple) {
          --icon-color: #6f42c1;
          --icon-shadow: rgba(111, 66, 193, 0.3);
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon.text-success {
          color: #28a745 !important;
        }
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper:has(.text-success) {
          border-color: #28a745 !important;
          box-shadow: 0 0 8px rgba(40, 167, 69, 0.3) !important;
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon.text-info {
          color: #17a2b8 !important;
        }
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper:has(.text-info) {
          border-color: #17a2b8 !important;
          box-shadow: 0 0 8px rgba(23, 162, 184, 0.3) !important;
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon.text-warning {
          color: #ffc107 !important;
        }
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper:has(.text-warning) {
          border-color: #ffc107 !important;
          box-shadow: 0 0 8px rgba(255, 193, 7, 0.3) !important;
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon.text-danger {
          color: #dc3545 !important;
        }
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper:has(.text-danger) {
          border-color: #dc3545 !important;
          box-shadow: 0 0 8px rgba(220, 53, 69, 0.3) !important;
        }

        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon.text-purple {
          color: #6f42c1 !important;
        }
        body[data-sidebar-size="collapsed"]
          .sidebar-link.active
          .nav-icon-wrapper:has(.text-purple) {
          border-color: #6f42c1 !important;
          box-shadow: 0 0 8px rgba(111, 66, 193, 0.3) !important;
        }

        .nav-icon {
          font-size: 1rem;
          transition: color 0.15s ease;
        }

        .nav-text {
          font-weight: 500;
          font-size: 0.9rem;
          color: ${dark ? "#ffffff" : "#2c3e50"};
          white-space: nowrap;
          transition: color 0.15s ease, font-weight 0.15s ease;
        }

        .sidebar-link:hover .nav-text,
        .sidebar-link.active .nav-text {
          color: ${dark ? "#ffffff" : "#1a1d29"};
          font-weight: 600;
        }

        /* Remove the indicator completely */
        .nav-indicator {
          display: none !important;
        }

        /* Remove any active state borders/lines */
        .sidebar-link.active::before,
        .sidebar-link.active::after,
        .nav-link.active::before,
        .nav-link.active::after {
          display: none !important;
        }

        /* Remove any default Bootstrap active styles */
        .nav-link.active {
          border-left: none !important;
          border-right: none !important;
          border-top: none !important;
          border-bottom: none !important;
          box-shadow: none !important;
        }

        /* Ensure no active line appears */
        .startbar .nav-link.active,
        .startbar .sidebar-link.active {
          border: none !important;
          border-left: none !important;
          position: relative;
        }

        .startbar .nav-link.active::before,
        .startbar .sidebar-link.active::before {
          content: none !important;
          display: none !important;
        }

        /* Footer */
        .sidebar-footer {
          background: ${dark
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.02)"};
          border-top: none;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 12px 16px;
          z-index: 10;
        }

        .footer-content {
          text-align: center;
        }

        .footer-icon {
          color: ${dark ? "#007bff" : "#0056b3"};
        }

        /* Collapsed state fixes */
        body[data-sidebar-size="collapsed"] .logo-text,
        body[data-sidebar-size="collapsed"] .nav-text,
        body[data-sidebar-size="collapsed"] .menu-label-text {
          display: none !important;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        body[data-sidebar-size="collapsed"] .sidebar-footer .nav-text {
          display: none !important;
        }

        body[data-sidebar-size="collapsed"] .sidebar-footer {
          padding: 8px !important;
        }

        body[data-sidebar-size="collapsed"] .sidebar-footer .logout-icon {
          font-size: 1.2rem !important;
          margin: 0 auto !important;
        }

        body[data-sidebar-size="collapsed"] .startbar {
          width: 70px !important;
          overflow: hidden;
        }

        /* Ensure icons stay within sidebar bounds */
        body[data-sidebar-size="collapsed"] .nav-link {
          max-width: 100% !important;
          overflow: hidden !important;
        }

        body[data-sidebar-size="collapsed"] .nav-link-content {
          max-width: 100% !important;
          overflow: hidden !important;
        }

        /* Reduced spacing for collapsed sidebar */
        body[data-sidebar-size="collapsed"] .nav-item {
          margin-bottom: 1px !important;
        }

        body[data-sidebar-size="collapsed"] .nav-link {
          padding: 4px 8px !important;
          margin-bottom: 0 !important;
        }

        body[data-sidebar-size="collapsed"] .nav-icon-wrapper {
          margin-right: 0 !important;
          width: 24px !important;
          height: 24px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .nav-icon {
          font-size: 1.1rem !important;
          margin: 0 !important;
        }

        body[data-sidebar-size="collapsed"] .sidebar-footer {
          padding: 4px 8px !important;
          margin-top: 8px !important;
        }

        body[data-sidebar-size="collapsed"] .sidebar-footer .logout-btn {
          padding: 8px !important;
        }

        /* Compact spacing for collapsed sidebar */
        body[data-sidebar-size="collapsed"] .navbar-nav {
          gap: 0 !important;
        }

        body[data-sidebar-size="collapsed"] .startbar-menu {
          padding: 4px 0 !important;
        }

        body[data-sidebar-size="collapsed"] .startbar-collapse {
          padding: 0 !important;
        }

        /* Ensure perfect centering of icons when collapsed */
        body[data-sidebar-size="collapsed"] .nav-link-content {
          justify-content: center !important;
          padding: 0.25rem 0 !important;
          width: 100% !important;
          display: flex !important;
          align-items: center !important;
        }

        body[data-sidebar-size="collapsed"] .nav-icon-wrapper {
          margin: 0 !important;
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .nav-item {
          margin: 0.125rem 0 !important;
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .sidebar-link {
          width: 100% !important;
          overflow: hidden;
          display: flex !important;
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .menu-label {
          display: flex;
          justify-content: center;
          margin: 0.25rem 0 0.5rem 0 !important;
          padding: 0.75rem 0 !important;
          width: 100% !important;
        }

        body[data-sidebar-size="collapsed"] .menu-label-icon {
          margin: 0;
        }

        body[data-sidebar-size="collapsed"] .logo-container {
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .brand {
          padding: 1rem 0 !important;
          display: flex !important;
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .logo {
          width: auto !important;
          justify-content: center !important;
        }

        body[data-sidebar-size="collapsed"] .sidebar-link:hover {
          transform: none;
        }

        body[data-sidebar-size="collapsed"] .sidebar-link.active {
          border: none !important;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .startbar {
            position: fixed;
            left: -100%;
            transition: left 0.3s ease;
            z-index: 1000;
          }

          .startbar.show {
            left: 0;
          }
        }

        /* Fast, specific transitions only for necessary properties */
        .sidebar-link,
        .nav-icon-wrapper,
        .nav-icon,
        .nav-text {
          transition: background-color 0.15s ease, color 0.15s ease,
            border-color 0.15s ease, box-shadow 0.15s ease;
        }

        /* Remove any unnecessary global transitions */
        .logo {
          transition: background-color 0.15s ease, transform 0.15s ease;
        }

        /* Logout Button Styles */
        .logout-btn {
          background: transparent !important;
          border: none !important;
          width: 100% !important;
          text-align: left !important;
          cursor: pointer !important;
        }

        .logout-btn:hover {
          background: ${dark
            ? "rgba(220, 53, 69, 0.1)"
            : "rgba(220, 53, 69, 0.05)"} !important;
        }

        .logout-icon {
          width: 16px;
          height: 16px;
          filter: ${dark ? "invert(1)" : "none"};
        }

        /* Logout Modal Styles */
        .logout-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
        }

        .logout-modal {
          background: ${dark ? "#2c3e50" : "#ffffff"};
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 400px;
          width: 90%;
          animation: slideIn 0.3s ease;
        }

        .logout-modal-header {
          padding: 1.5rem 1.5rem 0 1.5rem;
          border-bottom: 1px solid ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
        }

        .logout-modal-header h5 {
          margin: 0;
          color: ${dark ? "#ffffff" : "#2c3e50"};
          font-weight: 600;
        }

        .logout-modal-body {
          padding: 1.5rem;
        }

        .logout-modal-body p {
          margin: 0;
          color: ${dark ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)"};
          font-size: 1rem;
        }

        .logout-modal-footer {
          padding: 0 1.5rem 1.5rem 1.5rem;
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }

        .logout-modal-footer .btn {
          padding: 0.5rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-modal-footer .btn-secondary {
          background: ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
          color: ${dark ? "#ffffff" : "#2c3e50"};
        }

        .logout-modal-footer .btn-secondary:hover {
          background: ${dark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"};
        }

        .logout-modal-footer .btn-danger {
          background: #dc3545;
          color: white;
        }

        .logout-modal-footer .btn-danger:hover {
          background: #c82333;
          transform: translateY(-1px);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </>
  );
}

export default Sidebar;
